import _Vue, { DirectiveOptions } from 'vue'

/**
 * 是否是base640
 */
function isBase64(src: string) {
  return /^data:/.test(src)
}

/**
 * 浏览器是否支持 webp
 */
function checkIsSupportWebp() {
  try {
    return (
      document
        .createElement('canvas')
        .toDataURL('image/webp', 0.5)
        .indexOf('data:image/webp') === 0
    )
  } catch (err) {
    return false
  }
}

const isSupportWebp = checkIsSupportWebp()

/**
 * 添加query
 */
function addQuery(url = '', query: string) {
  let prefix = ''
  if (url.indexOf('?') !== -1) {
    prefix = '&'
  } else {
    prefix = '?'
  }
  return url + prefix + query
}

/**
 * 匹配 background url
 */
const reg = /url\([^]+\)/g

/**
 * 高清的webp图片query
 */
const webpQuery = 'x-s3-process=image/format,webp'

/**
 * 高清图地址
 */
function hdSrc(originSrc: string): string {
  if (isSupportWebp) {
    return addQuery(originSrc, webpQuery)
  }
  return originSrc
}

/**
 *
 * @param originString 图片地址
 * @param effect 渐进的操作dom, 使图片平滑加载
 * @param opacity 初始的透明度
 */
const preLoadImgFactory = (
  originSrc: string,
  effect: any,
  useWebp: boolean
): any => {
  return (el: HTMLElement) => {
    // 如果是base64
    if (isBase64(originSrc)) {
      effect(originSrc)
      return
    }

    // 处理后的图片
    const effectedSrc = useWebp
      ? hdSrc(originSrc)
      : originSrc

    // 加载处理后的图片
    effect(effectedSrc)
  }
}

/**
 * 处理<img c-webp src="xx">的情况
 */
const processImgTag = (el: HTMLElement) => {
  const originSrc = el.getAttribute('src') || ''

  if (originSrc) {
    el.dataset['webpCache'] = originSrc

    const effect: any = (src: string) => {
      el.setAttribute('src', src)
    }

    const preload: any = preLoadImgFactory(
      originSrc,
      effect,
      true
    )
    preload(el)
  }
}

/**
 * 处理css里的图片
 * @param srcString: 表示v-webp里的表达式
 */
const processCssBackground = (
  el: HTMLElement,
  srcString: string
) => {
  const cssProps = window.getComputedStyle(el, null)
  const hasBgImg = cssProps
    .getPropertyValue('background')
    .includes('url(')

  let originSrc = ''
  let useWebp = false

  // 如果直接指定background-image了的话, 浏览器会直接加载原图, 没法阻止, 这个时候就不能用webp了, 避免浪费流量
  if (hasBgImg) {
    const url = cssProps
      .getPropertyValue('background')
      .match(reg)
    if (url) {
      originSrc = url[0].split('"')[1]
    }
  } else if (srcString) {
    originSrc = srcString
    // 在v-imgPro="xxx" 里的图片才能享受 webp压缩
    useWebp = true
  }

  if (!originSrc) return

  el.dataset['webpCache'] = originSrc

  const effect: any = (src: string) => {
    el.style.backgroundImage = `url("${src}")`
  }

  const preload: any = preLoadImgFactory(
    originSrc,
    effect,
    useWebp
  )
  preload(el)
}

/**
 * 入口函数
 * @param el
 * @param srcString 背景图的地址
 */
const processImg = (el: HTMLElement, srcString: string) => {
  /**
   * 避免子组件更新, 造成重复的图片处理
   */
  const webpCache = el.dataset['webpCache']
  if (webpCache) {
    return
  }

  /**
   * 判断 dom 节点类型
   */
  if (el.tagName.toLocaleLowerCase() === 'img') {
    /**
     * 处理img标签
     */
    processImgTag(el)
  } else {
    /**
     * 处理css里的图片
     */
    processCssBackground(el, srcString)
  }
}

/**
 * 指令配置
 */
const webp: DirectiveOptions = {
  inserted(el, binding) {
    processImg(el, binding.value)
  },

  update(el, binding) {
    processImg(el, binding.value)
  },
}

const install = (Vue: typeof _Vue) => {
  Vue.directive('webp', webp)
}

export default {
  install,
  webp,
}
